import React, { useState, useEffect, useRef } from "react";
import { NotificationData, notificationApi } from "../services/notificationApi";
import styles from "../styles/NotificationPopup.module.css";
import { appKitBlockchainService } from "../services/appkitBlockchain";

interface NotificationPopupProps {
  notifications: NotificationData[];
  onClose: (notificationId: string) => void;
  onAllClosed: () => void;
}

const NotificationPopup: React.FC<NotificationPopupProps> = ({ notifications, onClose, onAllClosed }) => {
  const [currentNotificationIndex, setCurrentNotificationIndex] = useState(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const currentNotification = notifications[currentNotificationIndex];

  // Reset scroll detection when notification changes
  useEffect(() => {
    setHasScrolledToBottom(false);
  }, [currentNotificationIndex]);

  // Check if user has scrolled to bottom
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px buffer
        setHasScrolledToBottom(isAtBottom);
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener("scroll", handleScroll);
      // Check initial state (content might be short)
      handleScroll();
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [currentNotification]);

  // Simple markdown-like parsing for basic formatting
  const parseContent = (content: string): string => {
    if (appKitBlockchainService.isTestnet) {
      content = content.replaceAll("https://bscscan.com", "https://testnet.bscscan.com");
    }
    // Basic markdown-like parsing
    return (
      content
        // Links [text](url) - must come before other formatting
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        // Bold text
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        // Italic text
        .replace(/\*(.*?)\*/g, "<em>$1</em>")
        // Code blocks
        .replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>")
        // Inline code
        .replace(/`(.*?)`/g, "<code>$1</code>")
        // Line breaks
        .replace(/\n/g, "<br>")
    );
  };

  const handleClose = async () => {
    if (!hasScrolledToBottom) {
      return; // Don't allow closing if not scrolled to bottom
    }
    setIsClosing(true);
    // Mark current notification as read.
    // No need to await here, proceed next even if the request fails.
    notificationApi.markNotificationAsRead(currentNotification.id).catch((error) => {
      console.error("Failed to mark notification as read:", error);
    });

    // Move to next notification or close all
    if (currentNotificationIndex < notifications.length - 1) {
      setCurrentNotificationIndex(currentNotificationIndex + 1);
      setIsClosing(false);
    } else {
      // All notifications processed
      onAllClosed();
    }
  };

  const getNotificationTypeClass = (type: string): string => {
    switch (type) {
      case "warning":
        return styles.warning;
      case "success":
        return styles.success;
      case "announcement":
        return styles.announcement;
      case "info":
      default:
        return styles.info;
    }
  };

  const getNotificationIcon = (type: string): string => {
    switch (type) {
      case "warning":
        return "⚠️";
      case "success":
        return "✅";
      case "announcement":
        return "📢";
      case "info":
      default:
        return "ℹ️";
    }
  };

  if (!currentNotification) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div
        className={`${styles.popup} ${getNotificationTypeClass(currentNotification.type)} ${
          isClosing ? styles.closing : ""
        }`}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.typeIcon}>{getNotificationIcon(currentNotification.type)}</div>
          <h3 className={styles.title}>{currentNotification.title}</h3>
          <div className={styles.counter}>
            {currentNotificationIndex + 1} of {notifications.length}
          </div>
        </div>

        {/* Content */}
        <div
          ref={contentRef}
          className={styles.content}
          dangerouslySetInnerHTML={{
            __html: parseContent(currentNotification.content),
          }}
        />

        {/* Scroll indicator */}
        {!hasScrolledToBottom && (
          <div className={styles.scrollIndicator}>
            <div className={styles.scrollText}>Please scroll to the bottom to continue</div>
            <div className={styles.scrollArrow}>↓</div>
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.timestamp}>{new Date(currentNotification.createdAt).toLocaleString()}</div>
          <button
            className={`${styles.closeButton} ${hasScrolledToBottom ? styles.enabled : styles.disabled}`}
            onClick={handleClose}
            disabled={!hasScrolledToBottom || isClosing}
          >
            {isClosing ? "Processing..." : currentNotificationIndex < notifications.length - 1 ? "Next" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPopup;
